import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Navbar.css";

import icon from "../../assets/icons/icon.png";
import line from "../../assets/images/line.avif";
import profilePic from "../../assets/images/profile_image.png"; // fallback avatar
import { isAdminRegistrationEnabled } from "../../utils/featureFlags";
import { isManagementAdminRole, setStoredAdminRole } from "../../utils/adminAuth";

function Navbar({ isAuthenticated, handleLogout, adminRole }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [adminData, setAdminData] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Toggle the dropdown (hamburger) open/close
  const toggleDropdown = (event) => {
    event.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  // Close the dropdown if user clicks outside
  const handleClickOutside = (event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target) &&
      !buttonRef.current.contains(event.target)
    ) {
      setIsExpanded(false);
    }
  };

  // Refresh token logic
  const refreshTokens = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken || refreshToken === "undefined" || refreshToken === "null") return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        return false;
      }
      const data = await response.json();
      localStorage.setItem("idToken", data.idToken);
      if (data.accessToken) {
        localStorage.setItem("accessToken", data.accessToken);
      }
      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }
      return true;
    } catch (err) {
      console.error("Failed to refresh tokens:", err);
      return false;
    }
  };

  // Logout
  const handleLogoutClick = useCallback(() => {
    setIsExpanded(false);
    handleLogout(); // Clears tokens
    navigate("/");   // Go back to home
  }, [handleLogout, navigate]);

  // On mount, add the click listener. On unmount, remove it.
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch admin data if logged in
  useEffect(() => {
    const fetchAdminData = async () => {
      const idToken = localStorage.getItem("idToken");
      if (!idToken) {
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/profile`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401) {
          // Attempt token refresh
          const tokenRefreshed = await refreshTokens();
          if (tokenRefreshed) {
            const newToken = localStorage.getItem("idToken");
            const retryResp = await fetch(`${process.env.REACT_APP_API_URL}/admin/profile`, {
              method: "GET",
              headers: {
                Authorization: `Bearer ${newToken}`,
                "Content-Type": "application/json",
              },
            });
            if (!retryResp.ok) {
              throw new Error("Failed to fetch admin data after token refresh");
            }
            const data = await retryResp.json();
            setStoredAdminRole(data?.role);
            setAdminData(data);
          } else {
            handleLogoutClick();
          }
        } else if (!response.ok) {
          throw new Error("Failed to fetch admin data");
        } else {
          const data = await response.json();
          setStoredAdminRole(data?.role);
          setAdminData(data);
        }
      } catch (err) {
        console.error("Error fetching admin data:", err);
      }
    };

    if (isAuthenticated) {
      fetchAdminData();
    }
  }, [handleLogoutClick, isAuthenticated]);

  // Helper to navigate
  const handleItemClick = (path) => {
    setIsExpanded(false);
    navigate(path);
  };

  // Resolve avatar URL
  const backendUrl = process.env.REACT_APP_API_URL || "";
  let avatarSrc = profilePic;
  if (adminData?.profilePicturePath || adminData?.profilePictureUrl) {
    const raw = adminData.profilePicturePath || adminData.profilePictureUrl;
    avatarSrc = raw.startsWith("http") ? raw : `${backendUrl}${raw}`;
  }

  const resolvedRole = adminRole || adminData?.role || null;
  const canManageAdmin = resolvedRole ? isManagementAdminRole(resolvedRole) : true;

  return (
    <>
      {/* NAVBAR */}
      <nav className={`navbar ${isExpanded ? "expanded" : ""}`}>
        <div className="navbar-container">
          {/* Left section: ZEAL */}
          <div className="nav-left">
            <div className="nav-item" onClick={() => navigate("/")}>
              ZEAL
            </div>
          </div>

          {/* Center: icon (hamburger) */}
          <div className="nav-center">
            <img
              src={icon}
              alt="Icon"
              className="nav-icon"
              onClick={toggleDropdown}
              ref={buttonRef}
              style={{ cursor: "pointer" }}
            />
          </div>

          {/* Right side: login logic (like the example) */}
          <div className="nav-right">
            {isAuthenticated ? (
              // Show admin's first_name + optional avatar
              <div className="nav-item" onClick={() => navigate("/admin-profile")}>
                {adminData ? adminData.first_name : "Loading..."}
                <img
                  src={avatarSrc}
                  alt="avatar"
                  className="nav-avatar"
                  onError={(e) => { e.target.src = profilePic; }}
                />
              </div>
            ) : (
              // If not authenticated => show "START" or "LOGIN"/"REGISTER" based on the route
              <>
                {location.pathname === "/" && (
                  <div className="nav-item" onClick={() => navigate("/login")}>
                    START
                  </div>
                )}
                {location.pathname === "/register" && (
                  <div className="nav-item">
                    <Link to="/login">Login</Link>
                  </div>
                )}
                {location.pathname === "/login" && (
                  <>
                    {isAdminRegistrationEnabled && (
                      <div className="nav-item">
                        <Link to="/register">Register</Link>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </nav>

      {/* DROPDOWN (expanded menu) */}
      {isExpanded && (
        <div className="dropdown-overlay" ref={dropdownRef} onClick={() => setIsExpanded(false)}>
          <div className="expanded-menu" onClick={(e) => e.stopPropagation()}>
            {isAuthenticated ? (
              <>
                <div className="line">
                  <img src={line} alt="Line" className="line-img" />
                </div>
                <div className="dropdown-item" onClick={() => handleItemClick("/dashboard")}>
                  DASHBOARD
                </div>
                {canManageAdmin && (
                  <div className="dropdown-item" onClick={() => handleItemClick("/manage-stadium")}>
                    MANAGE STADIUM
                  </div>
                )}
                {canManageAdmin && (
                  <div className="dropdown-item" onClick={() => handleItemClick("/create-game")}>
                    CREATE GAME
                  </div>
                )}
                <div className="dropdown-item" onClick={() => handleItemClick("/admin-profile")}>
                  PROFILE
                </div>
                <div className="dropdown-item" onClick={handleLogoutClick}>
                  LOG OUT
                </div>
              </>
            ) : (
              <>
                <div className="line">
                  <img src={line} alt="Line" className="line-img" />
                </div>
                <div className="dropdown-item" onClick={() => handleItemClick("/login")}>
                  LOGIN
                </div>
                {isAdminRegistrationEnabled && (
                  <div className="dropdown-item" onClick={() => handleItemClick("/register")}>
                    REGISTER
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
